/**
 * Snapshot der Auth-Parameter aus der URL – **beim Laden des Moduls**, nicht in
 * einem Effekt.
 *
 * Grund: supabase-js verarbeitet Tokens aus dem Hash beim Initialisieren und
 * raeumt den Hash danach per `history.replaceState` weg. Wer den Hash erst in
 * einem `useEffect` liest, findet ihn manchmal schon leer vor (StrictMode liest
 * zweimal) und weiss dann nicht mehr, ob es ein Invite-, Recovery- oder
 * Magic-Link war. Dieses Modul wird aus `main.tsx` vor allem anderen importiert.
 */

function readHash(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

function readQuery(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

const hash = readHash();
const query = readQuery();

export interface AuthLink {
  /** 'invite' | 'recovery' | 'magiclink' | 'signup' | null */
  type: string | null;
  /** Fehlertext von Supabase, falls der Link ungueltig oder abgelaufen ist. */
  error: string | null;
  errorCode: string | null;
  /** Implicit-Flow: Tokens stehen im Hash. */
  hasTokens: boolean;
  /** PKCE-Flow: Code steht im Query-String. */
  hasCode: boolean;
  accessToken: string | null;
  refreshToken: string | null;
}

export const authLink: AuthLink = {
  type: hash.get('type') ?? query.get('type'),
  error: hash.get('error_description') ?? hash.get('error') ?? query.get('error_description'),
  errorCode: hash.get('error_code') ?? query.get('error_code'),
  hasTokens: Boolean(hash.get('access_token')),
  hasCode: Boolean(query.get('code')),
  accessToken: hash.get('access_token'),
  refreshToken: hash.get('refresh_token'),
};

/**
 * Hash sofort aus der Adresszeile entfernen.
 *
 * Zweck ist doppelt: Tokens gehoeren nicht in URL und History, und supabase-js
 * sieht damit eine saubere URL – die Session wird ausschliesslich ueber
 * `consumeAuthTokens()` gesetzt, statt sich auf `detectSessionInUrl` zu
 * verlassen. Der Query-String bleibt unangetastet, sonst zerschiesst es den
 * PKCE-Pfad (`?code=`).
 */
if (typeof window !== 'undefined' && window.location.hash && (authLink.hasTokens || authLink.error)) {
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
}

/** Braucht der User nach diesem Link zuerst ein Passwort? */
export const linkNeedsPassword = authLink.type === 'invite' || authLink.type === 'recovery';

/** Menschenlesbare Fassung typischer Supabase-Fehlercodes. */
export function describeAuthError(link: AuthLink = authLink): string | null {
  if (!link.error) return null;
  if (link.errorCode === 'otp_expired') {
    return 'Der Link ist abgelaufen oder wurde schon benutzt. Bitte einen neuen anfordern.';
  }
  if (link.errorCode === 'access_denied') {
    return 'Der Link ist nicht mehr gültig. Bitte einen neuen anfordern.';
  }
  return link.error;
}
